/**
 * The demo's one orchestrated load reveal (SKE-4166, phase 7).
 *
 * ONE MOMENT, NOT CONFETTI
 * ------------------------
 * A single staggered entrance on first paint buys more than a dozen scattered
 * micro-interactions, so this is the *only* load animation the demo shell adds:
 * the board arrives, then the honesty badge, then the way into the marketer's
 * side. Everything else that moves in the demo is a response to something the
 * visitor did, not to the page existing.
 *
 * The vocabulary lives here rather than being retyped at each site so the three
 * steps stay in one rhythm — change `DEMO_REVEAL_STEP_MS` and the whole reveal
 * re-times together.
 *
 * REDUCED MOTION
 * --------------
 * Every class is behind Tailwind's `motion-safe:` variant, so under
 * `prefers-reduced-motion: reduce` no animation is applied at all and each
 * element paints in its final state immediately. That is stronger than
 * shortening a duration: there is no animation to interrupt, and no
 * `fill-mode` holding an element invisible through its delay.
 *
 * WHY NO TRANSFORM ON THE BOARD
 * -----------------------------
 * `animate-in` renders its `from` frame with a `transform`, and with
 * `fill-mode: both` that transform is held for the whole delay AND left applied
 * afterwards. A transform on an ancestor makes it the containing block for its
 * `position: fixed` descendants — which would silently re-anchor the approver
 * UI's own fixed elements (the queue-cleared confetti canvas among them). The
 * board therefore fades only; the slides are reserved for the demo's own
 * self-contained fixed chrome, which has no fixed descendants and no transform
 * of its own to be overwritten.
 */

import type { CSSProperties } from 'react'

/** Gap between reveal steps. Three steps ≈ 320ms of stagger, then done. */
export const DEMO_REVEAL_STEP_MS = 160

/**
 * `animate-in` builds an `animation` shorthand, which would win over an inline
 * `animation-delay` — so the delay is set through the variable that shorthand
 * reads. Same mechanism the marketer mirror's row stagger uses.
 */
export const demoRevealStyle = (step: number): CSSProperties =>
  ({
    '--tw-animation-delay': `${step * DEMO_REVEAL_STEP_MS}ms`,
  }) as CSSProperties

const REVEAL_BASE =
  'motion-safe:animate-in motion-safe:fade-in motion-safe:fill-mode-both motion-safe:duration-500'

/** Step 0 — the board itself. Opacity only; see the header. */
export const DEMO_REVEAL_FADE = REVEAL_BASE

/** Step 1 — chrome that belongs to the top edge. */
export const DEMO_REVEAL_DROP = `${REVEAL_BASE} motion-safe:slide-in-from-top-2`

/** Step 2 — chrome that belongs to the bottom edge. */
export const DEMO_REVEAL_RISE = `${REVEAL_BASE} motion-safe:slide-in-from-bottom-2`
